/**
 * The models a session may run, and the effort each of them takes (§9.3).
 *
 * One grammar, because the three are not independent: a model belongs to one
 * agent's CLI (§5.10), and `--effort` takes levels some models do not have —
 * Codex's older models stop at `xhigh` — so what has to be valid is a *triple*,
 * and validating the parts separately admits combinations no agent can be
 * launched with.
 *
 * The values are each CLI's own aliases (`claude --model`, `codex --model`,
 * `claude --effort`, `model_reasoning_effort`), so nothing translates between
 * this table and a launch command. The agent is implied by the model: the two
 * CLIs name their models differently, so a model id answers which agent runs it.
 */
import { AGENT_IDS, DEFAULT_AGENT, type AgentId } from './agents';

export type Choice = { value: string; label: string };

/** A model, the agent whose CLI it belongs to, and the efforts it may be given. */
export interface ModelSpec extends Choice {
  agent: AgentId;
  efforts: string[];
}

/** Highest first: it is the axis the user is trading cost and latency along. */
export const EFFORTS: Choice[] = [
  { value: 'max', label: 'Max' },
  { value: 'xhigh', label: 'X-High' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }
];

export const EFFORT_IDS: string[] = EFFORTS.map((e) => e.value);

const ALL = EFFORT_IDS;
/** Codex's older models stop at `xhigh` (`codex debug models`). */
const NO_MAX = EFFORT_IDS.filter((e) => e !== 'max');

export const MODELS: ModelSpec[] = [
  { value: 'fable', label: 'Fable', agent: 'claude-code', efforts: ALL },
  { value: 'opus', label: 'Opus', agent: 'claude-code', efforts: ALL },
  { value: 'sonnet', label: 'Sonnet', agent: 'claude-code', efforts: ALL },
  // Haiku 5.5 takes all five levels, like the larger tiers (it took none before).
  { value: 'haiku', label: 'Haiku', agent: 'claude-code', efforts: ALL },
  { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', agent: 'codex', efforts: ALL },
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra', agent: 'codex', efforts: ALL },
  { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', agent: 'codex', efforts: ALL },
  { value: 'gpt-5.5', label: 'GPT-5.5', agent: 'codex', efforts: NO_MAX },
  { value: 'gpt-5.2', label: 'GPT-5.2', agent: 'codex', efforts: NO_MAX }
];

export const MODEL_IDS: string[] = MODELS.map((m) => m.value);

/** One agent's models, in the order they are offered. */
export function modelsFor(agent: AgentId): ModelSpec[] {
  return MODELS.filter((m) => m.agent === agent);
}

/**
 * The agents an install actually offers (§5.10), from what its settings say.
 *
 * Never empty: an install with no agent could not start a session, so a stored
 * list that has emptied — every id in it withdrawn, or a document written by
 * hand — reads back as the default agent alone rather than as "none".
 */
export function enabledAgents(chosen: readonly string[] | undefined): AgentId[] {
  const kept = AGENT_IDS.filter((id) => chosen?.includes(id));
  return kept.length ? kept : [DEFAULT_AGENT];
}

/**
 * Every model those agents can be given, in the table's own order.
 *
 * This is what each picker offers instead of `MODELS`: with one agent switched
 * off, its models are not choices any more, and a list that still showed them
 * would be a list where half the answers fail at launch.
 */
export function modelsForAgents(chosen: readonly string[] | undefined): ModelSpec[] {
  const agents = new Set<string>(enabledAgents(chosen));
  return MODELS.filter((m) => agents.has(m.agent));
}

/**
 * Every (model, effort) the grammar admits, as **one** list of pairs.
 *
 * Because what has to be valid is the pair: not every model takes every level,
 * so two fields admit a combination no agent can be launched with — a model
 * pinned here with an effort inherited from somewhere else. One field cannot
 * express it.
 *
 * A model with no effort would appear once, bare, which is exactly the entry
 * the two-field form could not offer; none is in the table today.
 */
export const MODEL_EFFORT_OPTIONS: Choice[] = MODELS.flatMap((model) => {
  const efforts = effortsFor(model.value);
  if (!efforts.length) return [{ value: model.value, label: model.label }];
  return efforts.map((effort) => ({ value: `${model.value}:${effort.value}`, label: `${model.label} · ${effort.label}` }));
});

/** The same, for the agents an install offers — the others are not choices. */
export function modelEffortOptions(chosen: readonly string[] | undefined): Choice[] {
  const offered = new Set(modelsForAgents(chosen).map((model) => model.value));
  return MODEL_EFFORT_OPTIONS.filter((pair) => offered.has(pair.value.split(':')[0]));
}

/** The pair a stored `defaultModel` / `defaultEffort` spells. */
export function pairOf(model: string | null | undefined, effort: string | null | undefined): string {
  if (!model) return '';
  return effortsFor(model).length && effort ? `${model}:${effort}` : model;
}

/** The two values a chosen pair writes. A model with no efforts writes none. */
export function splitPair(pair: string): { defaultModel: string; defaultEffort: string | null } {
  const [model, effort] = pair.split(':');
  return { defaultModel: model, defaultEffort: effort ?? null };
}

/** A pair in words, for a row that shows one without offering it. */
export function pairLabel(model: string | null | undefined, effort: string | null | undefined): string {
  const pair = pairOf(model, effort);
  return MODEL_EFFORT_OPTIONS.find((one) => one.value === pair)?.label ?? (model ?? '');
}

/** The model a form starts from when its current one belongs to no enabled agent. */
export function firstModelFor(chosen: readonly string[] | undefined): string {
  return modelsForAgents(chosen)[0]?.value ?? MODELS[0].value;
}

/** The agent a model belongs to, or null for a model the grammar does not have. */
export function agentOfModel(model: string): AgentId | null {
  return MODELS.find((m) => m.value === model)?.agent ?? null;
}

/** The same, for a stored value that may predate the grammar: the default agent. */
export function agentForModel(model: string): AgentId {
  return agentOfModel(model) ?? DEFAULT_AGENT;
}

/** What a model may be given — empty when the model takes no effort. */
export function effortsFor(model: string): Choice[] {
  const spec = MODELS.find((m) => m.value === model);
  return spec ? EFFORTS.filter((e) => spec.efforts.includes(e.value)) : [];
}

/**
 * The effort a launch actually carries, or `null` when the pair does not exist —
 * so the flag is dropped rather than passed to a model that would reject it. A
 * stored `max` is kept through a switch to GPT-5.5 and back; it is the launch
 * that decides, not the form.
 */
export function launchEffort(model: string, effort: string): string | null {
  return effortsFor(model).some((e) => e.value === effort) ? effort : null;
}

/**
 * A model of the *other* agent when the user switches agents on a form: the same
 * position in that agent's list when it has one, its first model otherwise. The
 * form keeps a valid triple across the switch instead of showing a model the
 * agent picked cannot run.
 */
export function defaultModelFor(agent: AgentId): string {
  return modelsFor(agent)[0]?.value ?? MODELS[0].value;
}
